import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from 'api-clients/monolith';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { ExternalLinkIcon } from '@/components/icons/external-link';
import { enableOpeningSourcing } from '@/api-clients/update-opening-listing-status';
import { useToaster } from '@/components/base/toaster';
import { useAuthContext } from '@/contexts/auth-context';
import { queryKeys } from '@/query-keys';
import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { useOpeningDetails } from '@/contexts/opening-context';
import { reportBoundaryError } from '@/utils/sentry';
import {
  listingStateMessages,
  postingUnavailableMessages,
} from '@/components/pages/job-posting/posting-unavailable-messages';

/**
 * Names the opening's posting-ineligibility reason and offers its available action.
 * The eligibility helper gives inactive openings precedence over listing state.
 */
function PostingUnavailableBannerContent() {
  const intl = useIntl();
  const { opening } = useOpeningDetails();
  const { whoami } = useAuthContext();
  const queryClient = useQueryClient();
  const { notifyError } = useToaster();
  const refetchOpening = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.openingDetails.detail,
    });
  const enableSourcing = useMutation({
    mutationFn: enableOpeningSourcing,
    onSuccess: refetchOpening,
    onError: async (error: Error) => {
      if (error instanceof ApiError && error.status === 409) {
        await refetchOpening();
        return;
      }
      const errors: unknown = error instanceof ApiError && error.body?.errors;
      notifyError({
        message:
          error instanceof ApiError &&
          error.status === 422 &&
          Array.isArray(errors) &&
          errors.every(
            (message): message is string => typeof message === 'string',
          ) &&
          errors.length > 0
            ? errors.join(', ')
            : intl.formatMessage({
                defaultMessage: "We couldn't enable sourcing. Try again.",
                description:
                  'Sourcing could not be enabled; the recruiter can retry',
              }),
      });
    },
  });
  const { isActive: isOpeningActive, url: openingUrl } = opening;
  const canEnableSourcing =
    whoami?.account_listing_status_active === true &&
    isOpeningActive &&
    opening.canManage === true &&
    opening.listingStatus != null &&
    opening.listingStatus !== 'public';

  const reason = opening.postingIneligibilityReason;
  if (!reason) return null;
  if (reason in listingStateMessages) {
    const state = intl.formatMessage(
      listingStateMessages[reason as keyof typeof listingStateMessages],
    );
    return (
      <Banner
        color="rose"
        title={intl.formatMessage(postingUnavailableMessages.title, { state })}
        description={intl.formatMessage(
          postingUnavailableMessages.description,
          { state },
        )}
        button={
          canEnableSourcing ? (
            <Button
              variant="secondary"
              disabled={enableSourcing.isPending}
              onClick={() =>
                opening.listingStatus &&
                enableSourcing.mutate({
                  openingId: opening.id,
                  expectedListingStatus: opening.listingStatus,
                })
              }
            >
              <FormattedMessage
                defaultMessage="Enable Sourcing"
                description="CTA to re-enable paid sourcing channels for this opening"
              />
            </Button>
          ) : (
            openingUrl && (
              <Button variant="secondary" asChild>
                <Link to={openingUrl} target="_blank">
                  <ExternalLinkIcon className="text-fx-neutral-900" />
                  <FormattedMessage {...postingUnavailableMessages.edit} />
                </Link>
              </Button>
            )
          )
        }
      />
    );
  }

  return (
    <Banner
      color="rose"
      title={
        !isOpeningActive
          ? intl.formatMessage({
              defaultMessage: 'Opening Inactive — Job Posting Disabled',
              description:
                'Warning title: this requisition is off and cannot post to boards',
            })
          : intl.formatMessage({
              defaultMessage: 'Sourcing Disabled',
              description:
                'Warning title: org turned off paid sourcing for this role only',
            })
      }
      description={
        !isOpeningActive
          ? intl.formatMessage({
              defaultMessage:
                'This opening is inactive. Posting to job boards and sourcing campaigns are paused. Activate the opening to resume hiring.',
              description:
                'Explains that inactive jobs stop all distribution until reactivated',
            })
          : intl.formatMessage({
              defaultMessage:
                'Sourcing is turned off for this opening. Job posting and campaigns are paused until sourcing is enabled.',
              description:
                'Explains that the account disabled sourcing spend while the job stays open',
            })
      }
      button={
        openingUrl && (
          <Button variant="secondary" asChild>
            <Link to={openingUrl} target="_blank">
              <ExternalLinkIcon className="text-fx-neutral-900" />
              {!isOpeningActive ? (
                <FormattedMessage
                  defaultMessage="Activate opening"
                  description="CTA to turn the requisition back on from the employer job form"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="Enable Sourcing"
                  description="CTA to re-enable paid sourcing channels for this opening"
                />
              )}
            </Link>
          </Button>
        )
      }
    />
  );
}

/**
 * Wrapped in its own Suspense boundary so its `useOpeningDetails()` call
 * doesn't block sibling sections (AutomationStatusBanner, Analytics, etc.)
 * in `ChannelsContent` from mounting and firing their own fetches.
 */
export function PostingUnavailableBanner() {
  return (
    <ErrorBoundary
      onError={reportBoundaryError('channels.posting-unavailable-banner')}
      fallback={null}
    >
      <Suspense fallback={null}>
        <PostingUnavailableBannerContent />
      </Suspense>
    </ErrorBoundary>
  );
}
